import { SplitFactory } from '@splitsoftware/splitio-react';
import { createContext } from 'react';
import { useSelector } from 'react-redux';

import {
  FEATUREFLAG_API_KEY,
  FEATUREFLAG_URL,
  FEATUREFLAG_MOCK_FEATURES
} from '~/common/helpers/environment';
import storage from '~/common/helpers/storage';
import WebAnalytics from '~/common/helpers/webAnalytics';

type UserData = {
  email: string;
};

const SplitAttributesContext = createContext<SplitIO.Attributes>({
  key: 'anonymous'
});

const getKey = (userData?: UserData): string => {
  if (userData?.email) return userData.email;

  const keySession = storage.get('userToggleKeySession', false);

  if (keySession) return keySession;

  const anonKeySession = `anon-${new Date().getTime()}`;

  storage.set('userToggleKeySession', anonKeySession);

  return anonKeySession;
};

const SplitProvider = ({ children }: { children: JSX.Element }) => {
  // @ts-expect-error redux state is untyped
  const authUserData = useSelector(state => state.authentication.userData);
  // @ts-expect-error redux state is untyped
  const authIsSignedIn = useSelector(state => state.authentication.isSignedIn);
  // @ts-expect-error redux state is untyped
  const realm = useSelector(state => state.application.realm?.realm);
  // @ts-expect-error redux state is untyped
  const cluster = useSelector(state => state.realms.getRealm?.cluster);

  const key = getKey(authUserData);

  const config: SplitIO.IBrowserSettings = {
    core: {
      authorizationKey: FEATUREFLAG_API_KEY,
      key,
      trafficType: authIsSignedIn ? 'user' : 'anonymous'
    },
    urls: {
      sdk: FEATUREFLAG_URL,
      events: FEATUREFLAG_URL,
      auth: FEATUREFLAG_URL
    },
    features: FEATUREFLAG_MOCK_FEATURES as SplitIO.MockedFeaturesMap
  };

  const attributes = {
    key,
    realm: realm || '',
    cluster: cluster || ''
  };

  try {
    return (
      <SplitAttributesContext.Provider value={attributes}>
        <SplitFactory config={config} attributes={attributes}>
          {children}
        </SplitFactory>
      </SplitAttributesContext.Provider>
    );
  } catch (error) {
    WebAnalytics.sendEvent('[Global] (Feature Flag) Provider Error', { error });
    return children;
  }
};

export { SplitAttributesContext };
export default SplitProvider;
